/* eslint-disable no-undef */
class CartDrawer extends HTMLElement {
  constructor () {
    super()
    this.openScrollPosition = 0

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close())
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this))
    this.setHeaderCartIconAccessibility()

    // Defer promo modal open until after the drawer fully closes
    this.addEventListener('click', (event) => {
      const trigger = event.target.closest('.promo-modal-trigger')
      if (!trigger) return

      event.preventDefault()
      event.stopPropagation()

      let fallbackTimer
      const openPromoAfterClose = () => {
        if (fallbackTimer) {
          clearTimeout(fallbackTimer)
          fallbackTimer = null
        }
        // Create a temporary trigger so the global promo-bar listener handles opening
        const tempTrigger = document.createElement('button')
        tempTrigger.type = 'button'
        tempTrigger.className = 'promo-modal-trigger sr-only'
        const sr = document.createElement('span')
        sr.className = 'sr-only'
        sr.textContent = 'Open promo modal'
        tempTrigger.appendChild(sr)
        document.body.appendChild(tempTrigger)
        tempTrigger.click()
        tempTrigger.remove()
      }

      this.addEventListener('transitionend', openPromoAfterClose, { once: true })
      fallbackTimer = setTimeout(openPromoAfterClose, 350)
      this.close()
    })

    // Setup references that may be re-bound on render
    this._bindScrollableRefs()
  }

  _bindScrollableRefs () {
    this.items = this.querySelector('#CartDrawer-CartItems')
    const itemsRoot = this.querySelector('cart-drawer-items')
    const lineItemsVisible = itemsRoot && !itemsRoot.classList.contains('hidden')

    this.scrollShell = this.querySelector('.cart-drawer__scroll-shell')
    this.form = lineItemsVisible
      ? (this.querySelector('#CartDrawer-Scroll') || this.querySelector('#CartDrawer-Form'))
      : this.querySelector('.cart-drawer__empty')

    const faderRoot = this.scrollShell || this.form
    if (faderRoot) {
      this.topFader = faderRoot.querySelector('.cart-drawer__fader--top')
      this.bottomFader = faderRoot.querySelector('.cart-drawer__fader--bottom')
    } else {
      this.topFader = null
      this.bottomFader = null
    }

    if (this.form) {
      this.topSentinel = this.form.querySelector('.cart-drawer__sentinel--top')
      this.bottomSentinel = this.form.querySelector('.cart-drawer__sentinel--bottom')
    } else {
      this.topSentinel = null
      this.bottomSentinel = null
    }
  }

  _initFaders () {
    this._cleanupFaders()
    this._bindScrollableRefs()
    if (!this.form || !this.topFader || !this.bottomFader) return

    const isScrollable = this.form.scrollHeight > this.form.clientHeight
    if (!isScrollable) {
      this.topFader.classList.remove('show')
      this.bottomFader.classList.remove('show')
      return
    }

    if (this.topFader) this.topFader.classList.add('show')
    if (this.bottomFader) this.bottomFader.classList.add('show')

    if (this.topSentinel && this.bottomSentinel && 'IntersectionObserver' in window) {
      this._faderObserver = new IntersectionObserver((entries) => {
        for (const entry of entries) {
          if (entry.target === this.topSentinel) {
            // If top sentinel is visible, we're at the very top; hide top fader
            if (entry.isIntersecting) {
              this.topFader.classList.remove('show')
            } else {
              this.topFader.classList.add('show')
            }
          } else if (entry.target === this.bottomSentinel) {
            // If bottom sentinel is visible, near bottom; hide bottom fader
            if (entry.isIntersecting) {
              this.bottomFader.classList.remove('show')
            } else {
              this.bottomFader.classList.add('show')
            }
          }
        }
      }, {
        root: this.form,
        threshold: 0.01,
      })

      this._faderObserver.observe(this.topSentinel)
      this._faderObserver.observe(this.bottomSentinel)
      return
    }

    // Fallback: scroll handler
    const onScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = this.form
      const atTop = scrollTop <= 0
      const distanceFromBottom = scrollHeight - (scrollTop + clientHeight)
      const nearBottom = Math.ceil(distanceFromBottom) <= 5
      if (this.topFader) this.topFader.classList.toggle('show', !atTop)
      if (this.bottomFader) this.bottomFader.classList.toggle('show', !nearBottom)
    }
    this._scrollHandler = onScroll
    this.form.addEventListener('scroll', this._scrollHandler)
    // Initial state
    onScroll()
  }

  _cleanupFaders () {
    if (this._faderObserver) {
      try { this._faderObserver.disconnect() } catch (e) {}
      this._faderObserver = null
    }
    if (this.form && this._scrollHandler) {
      this.form.removeEventListener('scroll', this._scrollHandler)
      this._scrollHandler = null
    }
    if (this.topFader) this.topFader.classList.remove('show')
    if (this.bottomFader) this.bottomFader.classList.remove('show')
  }

  _teardownFormResizeObserver () {
    if (this._formResizeObserver) {
      try { this._formResizeObserver.disconnect() } catch (e) {}
      this._formResizeObserver = null
    }
  }

  _setupFormResizeObserver () {
    this._teardownFormResizeObserver()
    if (typeof ResizeObserver === 'undefined') return
    this._bindScrollableRefs()
    const scrollRoot = this.form
    if (!scrollRoot) return
    let debounceTimer = null
    this._formResizeObserver = new ResizeObserver(() => {
      if (!this.classList.contains('active')) return
      if (debounceTimer) clearTimeout(debounceTimer)
      debounceTimer = setTimeout(() => {
        debounceTimer = null
        this._initFaders()
      }, 50)
    })
    this._formResizeObserver.observe(scrollRoot)
  }

  _cleanupCrossSellAccordion () {
    if (this._crossSellExpandTimer) {
      clearTimeout(this._crossSellExpandTimer)
      this._crossSellExpandTimer = null
    }
    if (this._crossSellWillChangeTimer) {
      clearTimeout(this._crossSellWillChangeTimer)
      this._crossSellWillChangeTimer = null
    }
    if (this._crossSellAccordionUnlockTimer) {
      clearTimeout(this._crossSellAccordionUnlockTimer)
      this._crossSellAccordionUnlockTimer = null
    }
    this._crossSellAccordionBusy = false
    const panel = this.querySelector('[data-cross-sells-panel]')
    if (panel) panel.style.removeProperty('will-change')
    const list = this.querySelector('.cart-drawer__cross-sells-list')
    if (list) list.classList.remove('cart-drawer__cross-sells-list--collapsing')
    const trigger = this.querySelector('[data-cross-sells-trigger]')
    if (trigger) {
      trigger.style.pointerEvents = ''
      trigger.removeAttribute('aria-disabled')
    }
    if (this._crossSellCleanup) {
      this._crossSellCleanup()
      this._crossSellCleanup = null
    }
  }

  _setupCrossSellAccordion ({ autoExpand = false } = {}) {
    this._cleanupCrossSellAccordion()
    const root = this.querySelector('[data-cross-sells-root]')
    if (!root || !root.hasAttribute('data-accordion')) return
    const trigger = root.querySelector('[data-cross-sells-trigger]')
    const panel = root.querySelector('[data-cross-sells-panel]')
    const chevron = root.querySelector('[data-cross-sells-chevron]')
    if (!trigger || !panel) return

    const list = root.querySelector('.cart-drawer__cross-sells-list')

    const hasStagger = root.hasAttribute('data-stagger-animation')
    const CROSS_SELL_EXPAND_LOCK_MS = hasStagger ? 800 : 350
    const CROSS_SELL_COLLAPSE_LOCK_MS = hasStagger ? 300 : 350

    const endCrossSellBusy = () => {
      this._crossSellAccordionBusy = false
      if (this._crossSellAccordionUnlockTimer) {
        clearTimeout(this._crossSellAccordionUnlockTimer)
        this._crossSellAccordionUnlockTimer = null
      }
      trigger.style.pointerEvents = ''
      trigger.removeAttribute('aria-disabled')
    }

    const applyExpanded = (next, opts = {}) => {
      const silent = opts.silent === true
      if (!silent && this._crossSellAccordionBusy) return

      if (this._crossSellWillChangeTimer) {
        clearTimeout(this._crossSellWillChangeTimer)
        this._crossSellWillChangeTimer = null
      }
      panel.style.willChange = 'grid-template-rows'
      const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches
      const expandLock = reduceMotion ? 50 : CROSS_SELL_EXPAND_LOCK_MS
      const collapseLock = reduceMotion ? 50 : CROSS_SELL_COLLAPSE_LOCK_MS

      if (next) {
        list?.classList.remove('cart-drawer__cross-sells-list--collapsing')
        panel.classList.add('cart-drawer__cross-sells-panel--open')
        trigger.setAttribute('aria-expanded', 'true')
        if (chevron) chevron.classList.remove('rotate-180')
        if (!silent) {
          this._crossSellAccordionBusy = true
          trigger.style.pointerEvents = 'none'
          trigger.setAttribute('aria-disabled', 'true')
          if (this._crossSellAccordionUnlockTimer) clearTimeout(this._crossSellAccordionUnlockTimer)
          this._crossSellAccordionUnlockTimer = setTimeout(() => {
            endCrossSellBusy()
          }, expandLock)
        }
      } else {
        list?.classList.add('cart-drawer__cross-sells-list--collapsing')
        panel.classList.remove('cart-drawer__cross-sells-panel--open')
        trigger.setAttribute('aria-expanded', 'false')
        if (chevron) chevron.classList.add('rotate-180')
        if (!silent) {
          this._crossSellAccordionBusy = true
          trigger.style.pointerEvents = 'none'
          trigger.setAttribute('aria-disabled', 'true')
          if (this._crossSellAccordionUnlockTimer) clearTimeout(this._crossSellAccordionUnlockTimer)
          this._crossSellAccordionUnlockTimer = setTimeout(() => {
            list?.classList.remove('cart-drawer__cross-sells-list--collapsing')
            endCrossSellBusy()
          }, collapseLock)
        }
      }

      this._crossSellWillChangeTimer = setTimeout(() => {
        panel.style.removeProperty('will-change')
        this._crossSellWillChangeTimer = null
      }, 350)
    }

    applyExpanded(false, { silent: true })

    const onClick = () => {
      if (this._crossSellAccordionBusy) return
      if (this._crossSellExpandTimer) {
        clearTimeout(this._crossSellExpandTimer)
        this._crossSellExpandTimer = null
      }
      const isOpen = panel.classList.contains('cart-drawer__cross-sells-panel--open')
      applyExpanded(!isOpen)
    }
    trigger.addEventListener('click', onClick)

    if (autoExpand) {
      this._crossSellExpandTimer = setTimeout(() => {
        this._crossSellExpandTimer = null
        applyExpanded(true)
      }, 2000)
    }

    this._crossSellCleanup = () => {
      trigger.removeEventListener('click', onClick)
    }
  }

  setHeaderCartIconAccessibility () {
    const cartLink = document.querySelector('#cart-icon-bubble')
    cartLink.setAttribute('role', 'button')
    cartLink.setAttribute('aria-haspopup', 'dialog')
    cartLink.addEventListener('click', (event) => {
      event.preventDefault()
      this.refreshAndOpen(cartLink)
    })
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault()
        this.refreshAndOpen(cartLink)
      }
    })
  }

  refreshAndOpen (triggeredBy) {
    this._skipCrossSellAutoExpand = false
    this.open(triggeredBy)
  }

  open (triggeredBy) {
    this.openScrollPosition = window.scrollY
    if (triggeredBy) this.setActiveElement(triggeredBy)
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary')
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote)

    window.dispatchEvent(new CustomEvent('close:modal'))

    const shouldAutoExpand = this._skipCrossSellAutoExpand !== true
    setTimeout(() => {
      this.classList.add('animate', 'active')
      this.classList.remove('opacity-0', 'invisible')
      this.querySelector('#CartDrawer-Overlay')?.classList.add('pointer-events-auto')
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          this._initFaders()
          this._setupFormResizeObserver()
          this._setupCrossSellAccordion({ autoExpand: shouldAutoExpand })
        })
      })
    }, 1)

    allowPageScroll(false)

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer')
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close')
        trapFocus(containerToTrapFocusOn, focusElement)
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            this._initFaders()
          })
        })
      },
      { once: true },
    )

    window.dispatchEvent(new CustomEvent('show:page-obscure'))
    window.dispatchEvent(new CustomEvent('prevent:page-scroll'))
  }

  close () {
    this._skipCrossSellAutoExpand = false
    this.classList.remove('active')
    this.classList.add('opacity-0', 'invisible')
    this.querySelector('#CartDrawer-Overlay')?.classList.remove('pointer-events-auto')
    removeTrapFocus(this.activeElement)
    allowPageScroll(true)

    window.dispatchEvent(new CustomEvent('hide:page-obscure'))
    window.dispatchEvent(new CustomEvent('allow:page-scroll'))

    this._cleanupFaders()
    this._teardownFormResizeObserver()
    this._cleanupCrossSellAccordion()
  }

  refreshChrome ({ autoExpand = false } = {}) {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this._initFaders()
        this._setupFormResizeObserver()
        this._setupCrossSellAccordion({ autoExpand })
      })
    })
  }

  setSummaryAccessibility (cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button')
    cartDrawerNote.setAttribute('aria-expanded', 'false')

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id)
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'))
    })

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape)
  }

  renderContents (parsedState) {
    const drawerInner = this.querySelector('.drawer__inner')
    if (drawerInner) {
      drawerInner.classList.contains('is-empty') && drawerInner.classList.remove('is-empty')
    }
    this.productId = parsedState.id
    const sectionsToRender = this.getSectionsToRender()
    sectionsToRender.forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id)
      if (sectionElement && parsedState.sections && parsedState.sections[section.id]) {
        sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector)
      }
    })

    setTimeout(() => {
      const overlay = this.querySelector('#CartDrawer-Overlay')
      if (overlay) {
        overlay.addEventListener('click', this.close.bind(this))
      }
      this.open()
    })
  }

  getSectionInnerHTML (html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML
  }

  getSectionsToRender () {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ]
  }

  getSectionDOM (html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector)
  }

  setActiveElement (element) {
    this.activeElement = element
  }
}

customElements.define('cart-drawer', CartDrawer)

window.addEventListener('cart:updated', () => {
  setTimeout(() => {
    if (window.tentboxInitCartDrawerFaders) window.tentboxInitCartDrawerFaders(document)
    const drawer = document.querySelector('cart-drawer')
    if (drawer && typeof drawer.refreshChrome === 'function') drawer.refreshChrome()
  }, 0)
})
